import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { PackageService } from '../../../services/package.service';
import { SortOrderService, SortOrderRearrangePayload } from '../../../services/sort-order.service';
import { FleetService } from '../../../fleet/services/fleet.service';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { Package, PackageDiscount } from '../../../shared/types';

export interface MostUsedRow {
  id: string;
  label: string;
  sublabel?: string;
  pill?: string;
  kind?: 'package' | 'fleet' | 'discount';
  selected?: boolean;
}

@Component({
  selector: 'app-most-used-services-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule],
  providers: [NotificationService],
  templateUrl: './most-used-services-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './most-used-services-modal.component.scss',
})
export class MostUsedServicesModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() mode: 'drag' | 'default-select' = 'drag';
  @Input() locationId: string | null = null;
  @Input() sortOrderId: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  loading = false;
  saving = false;

  singleWash: MostUsedRow[] = [];
  monthlyWash: MostUsedRow[] = [];
  prepaid: MostUsedRow[] = [];
  addOns: MostUsedRow[] = [];
  fleets: MostUsedRow[] = [];
  discounts: MostUsedRow[] = [];
  private initialDefaultPackageIds = new Set<string>();
  private initialDefaultFleetIds = new Set<string>();

  constructor(
    private packageService: PackageService,
    private sortOrderService: SortOrderService,
    private fleetService: FleetService,
    private messageService: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen && this.locationId) {
      this.loadAll();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isDefaultSelectMode(): boolean {
    return this.mode === 'default-select';
  }

  onOverlayClick(): void {
    this.emitClose();
  }

  onCloseBtn(): void {
    this.emitClose();
  }

  private emitClose(): void {
    this.close.emit();
  }

  onCancel(): void {
    this.emitClose();
  }

  onSave(): void {
    if (this.mode === 'default-select') {
      this.saveDefaultSelections();
      return;
    }
    if (!this.sortOrderId || this.saving) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing data',
        detail: 'Sort order is not ready. Try again.',
      });
      return;
    }
    const body = this.buildRearrangePayload();
    this.saving = true;
    this.sortOrderService
      .rearrange(this.sortOrderId, body)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(res => {
          if (!res || res.status === false) {
            return of(false);
          }
          if (!this.locationId || !this.sortOrderId) {
            return of(true);
          }
          return this.sortOrderService
            .updateLocationCustomSortOrder(this.locationId, this.sortOrderId)
            .pipe(
              map(prefRes => !!prefRes && prefRes.status !== false),
              catchError(err => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Save failed',
                  detail: err.error?.message || 'Custom sort preference could not be saved.',
                });
                return of(false);
              })
            );
        }),
        catchError(err => {
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: err.error?.message || 'Could not save order.',
          });
          return of(null);
        })
      )
      .subscribe(ok => {
        this.saving = false;
        if (ok) {
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Display order updated.',
          });
          this.saved.emit();
          this.emitClose();
        }
      });
  }

  private saveDefaultSelections(): void {
    if (!this.locationId || this.saving) {
      return;
    }
    const selectedPackageIds = this.getSelectedIdsByKind('package');

    const packageChanged = !this.setsEqual(selectedPackageIds, this.initialDefaultPackageIds);

    if (!packageChanged) {
      this.messageService.add({
        severity: 'info',
        summary: 'No changes',
        detail: 'No default changes to save.',
      });
      this.emitClose();
      return;
    }

    this.saving = true;

    const calls: Observable<any>[] = [];
    if (packageChanged) {
      const package_ids = Array.from(selectedPackageIds);
      calls.push(
        this.packageService
          .setDefaultPackagesForLocationBatch(this.locationId!, { package_ids, is_default: true })
          .pipe(catchError(() => of(null)))
      );
    }

    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Most used services updated.',
          });
          this.saved.emit();
          this.emitClose();
        },
        error: () => {
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: 'Could not save changes.',
          });
        },
      });
  }

  private buildRearrangePayload(): SortOrderRearrangePayload {
    const singleWashOut = this.buildUniqueSortRows([...this.singleWash, ...this.prepaid]);
    const monthlyWashOut = this.buildUniqueSortRows(this.monthlyWash);
    const addOnOut = this.buildUniqueSortRows(this.addOns);
    const fleetOut = this.buildUniqueSortRows(this.fleets);
    const discountOut = this.buildUniqueSortRows(this.discounts);

    return {
      single_wash: singleWashOut,
      monthly_wash: monthlyWashOut,
      add_on: addOnOut,
      fleet: fleetOut,
      discount: discountOut,
    };
  }

  /** Ensure each id appears once in payload with stable order. */
  private buildUniqueSortRows(rows: MostUsedRow[]): { id: string; sort_order: number }[] {
    const out: { id: string; sort_order: number }[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
      const id = String(row.id || '').trim();
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      out.push({ id, sort_order: out.length + 1 });
    }
    return out;
  }

  private loadAll(): void {
    if (!this.locationId) {
      return;
    }
    this.loading = true;
    this.singleWash = [];
    this.monthlyWash = [];
    this.prepaid = [];
    this.addOns = [];
    this.fleets = [];
    this.discounts = [];

    forkJoin({
      pkgs: this.packageService
        .getPackagesByLocation(this.locationId, {
          washType: PackageService.SALES_PANEL_VIEW_BY_LOCATION_WASH_TYPES,
        })
        .pipe(catchError(() => of([] as Package[]))),
      addOnPkgs: this.packageService
        .getAddOnPackagesByLocation(this.locationId)
        .pipe(catchError(() => of([] as Package[]))),
      fleets: this.fleetService
        .queryFleets({ location_id: this.locationId })
        .pipe(catchError(() => of({ fleets: [], pagination: { page: 1, limit: 0, total: 0 } }))),
      discounts: this.packageService
        .getDiscounts({ location_id: this.locationId })
        .pipe(
          catchError(() =>
            of({ status: false, data: [] } as { status: boolean; data: PackageDiscount[] })
          )
        ),
      defaultPackages:
        this.mode === 'default-select'
          ? this.packageService
              .getDefaultPackagesByLocation(this.locationId)
              .pipe(catchError(() => of({ status: false, data: [] as Package[] })))
          : of({ status: false, data: [] as Package[] }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ pkgs, addOnPkgs, fleets, discounts, defaultPackages }) => {
          this.hydrateFromPackages([...pkgs, ...addOnPkgs]);
          this.fleets = (fleets.fleets || []).map(f => ({
            id: f.id,
            label: f.clientName,
            sublabel: this.formatPrice(f.pricePerVehicle),
            kind: 'fleet',
            selected: false,
          }));
          const drows = Array.isArray(discounts.data) ? discounts.data : [];
          this.discounts = drows
            .filter(d => d.is_active !== false)
            .map(d => ({
              id: d.id,
              label: d.name,
              sublabel: this.formatDiscountSublabel(d),
              kind: 'discount',
              selected: false,
            }));
          if (this.mode === 'default-select') {
            this.initialDefaultFleetIds.clear();
            this.initialDefaultPackageIds = this.collectDefaultPackageIdsFromApi(
              defaultPackages?.data
            );
            this.applyDefaultsToRows();
          } else {
            this.initialDefaultFleetIds.clear();
            this.initialDefaultPackageIds.clear();
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private collectDefaultPackageIdsFromPackages(packages: Package[]): Set<string> {
    const out = new Set<string>();
    for (const p of packages) {
      const raw = (p as any as { is_default?: any }).is_default;
      const isDefault =
        raw === true || Number(raw) === 1 || String(raw ?? '').toLowerCase() === 'true';
      if (isDefault) {
        const id = String(p.id || '').trim();
        if (id) {
          out.add(id);
        }
      }
    }
    return out;
  }

  private collectDefaultPackageIdsFromApi(rows: any): Set<string> {
    if (!Array.isArray(rows)) {
      return new Set<string>();
    }
    const mapped = rows
      .map(row => String((row as { id?: any })?.id ?? '').trim())
      .filter(id => !!id);
    if (mapped.length > 0) {
      return new Set<string>(mapped);
    }
    // Backward fallback in case API shape changes and embeds defaults in package rows.
    return this.collectDefaultPackageIdsFromPackages(rows as Package[]);
  }

  private applyDefaultsToRows(): void {
    const mark = (rows: MostUsedRow[]): MostUsedRow[] =>
      rows.map(r => {
        if (r.kind === 'fleet') {
          return { ...r, selected: this.initialDefaultFleetIds.has(r.id) };
        }
        if (r.kind === 'package') {
          return { ...r, selected: this.initialDefaultPackageIds.has(r.id) };
        }
        return { ...r, selected: false };
      });
    this.singleWash = mark(this.singleWash);
    this.monthlyWash = mark(this.monthlyWash);
    this.prepaid = mark(this.prepaid);
    this.addOns = mark(this.addOns);
    this.fleets = mark(this.fleets);
    this.discounts = mark(this.discounts);
  }

  toggleSelection(row: MostUsedRow): void {
    if (this.mode !== 'default-select') {
      return;
    }
    const next = !row.selected;
    const setRows = (rows: MostUsedRow[]) =>
      rows.map(r => (r.kind === row.kind && r.id === row.id ? { ...r, selected: next } : r));
    if (row.kind === 'package') {
      this.singleWash = setRows(this.singleWash);
      this.monthlyWash = setRows(this.monthlyWash);
      this.prepaid = setRows(this.prepaid);
      this.addOns = setRows(this.addOns);
      return;
    }
    if (row.kind === 'fleet') {
      this.fleets = setRows(this.fleets);
      return;
    }
    if (row.kind === 'discount') {
      this.discounts = setRows(this.discounts);
    }
  }

  private getSelectedIdsByKind(kind: 'package' | 'fleet'): Set<string> {
    const rows =
      kind === 'fleet'
        ? this.fleets
        : [...this.singleWash, ...this.monthlyWash, ...this.prepaid, ...this.addOns];
    const out = new Set<string>();
    for (const row of rows) {
      if (row.kind === kind && row.selected) {
        out.add(row.id);
      }
    }
    return out;
  }

  private setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const id of a) {
      if (!b.has(id)) {
        return false;
      }
    }
    return true;
  }

  private hydrateFromPackages(packages: Package[]): void {
    for (const p of packages) {
      const t = (p.type || '').toLowerCase();
      if (t === 'add_on') {
        const firstVariant = p.variants?.find(v => !!v.single_list_price);
        const addonPrice = firstVariant?.single_list_price || p.single_list_price;
        this.addOns.push({
          id: p.id,
          label: p.package_name,
          sublabel: this.formatPrice(addonPrice) || 'Add-on',
          pill: p.variants?.some(v => v.allows_custom_price) ? 'Custom price' : undefined,
          kind: 'package',
          selected: false,
        });
        continue;
      }
      if (t === 'prepaid' || (t !== 'car_wash' && t !== 'add_on' && t !== 'detailing')) {
        this.prepaid.push({
          id: p.id,
          label: p.package_name,
          sublabel: this.formatPrice(p.single_list_price) || 'Prepaid',
          pill: p.family_plan ? 'Family' : undefined,
          kind: 'package',
          selected: false,
        });
        continue;
      }
      if (t === 'car_wash') {
        const w = (p.wash_types || []).map(x => String(x).toLowerCase());
        const hasSingle = w.some(x => x.includes('single'));
        const hasMonthly = w.some(x => x.includes('monthly') || x.includes('membership'));
        if (hasSingle) {
          this.singleWash.push({
            id: p.id,
            label: p.package_name,
            sublabel: this.formatPrice(p.single_list_price) || 'Single Wash',
            pill: p.is_defender ? 'Defender' : undefined,
            kind: 'package',
            selected: false,
          });
        }
        if (hasMonthly) {
          this.monthlyWash.push({
            id: p.id,
            label: p.package_name,
            sublabel: this.formatPrice(p.monthly_list_price, '/mo') || 'Monthly',
            pill: p.family_plan ? 'Family' : undefined,
            kind: 'package',
            selected: false,
          });
        }
        if (!hasSingle && !hasMonthly) {
          this.singleWash.push({
            id: p.id,
            label: p.package_name,
            sublabel: this.formatPrice(p.single_list_price) || 'Package',
            kind: 'package',
            selected: false,
          });
        }
      }
    }
  }

  private formatPrice(value: string | number | null | undefined, suffix = ''): string {
    const n =
      typeof value === 'number'
        ? value
        : typeof value === 'string'
          ? parseFloat(value.replace(/[$,]/g, '').trim())
          : NaN;
    if (!Number.isFinite(n) || n <= 0) {
      return '';
    }
    return `$${n.toFixed(2)}${suffix}`;
  }

  private formatDiscountSublabel(d: PackageDiscount): string {
    const dt = (d.discount_type || '').toLowerCase();
    if (dt === 'percentage' || dt === 'percent') {
      return `${d.discount_value || ''}`.replace(/%$/, '') + '% off';
    }
    return d.discount_value ? `$${d.discount_value}` : '';
  }

  onDrop(event: CdkDragDrop<MostUsedRow[]>): void {
    if (this.mode === 'default-select') {
      return;
    }
    // Lists are not grouped: only same-category reorder is allowed by the CDK; guard anyway.
    if (event.previousContainer !== event.container) {
      return;
    }
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }
}
